import React, { useEffect, useState, createContext, useContext } from "react";
import "./App.css";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { Provider, useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setUserToken, unSetUserToken } from "./features/authSlice";
import store from "./store/store";
import { Layout, Spin } from "antd";
import LoginPage from "./components/LoginPage";
import ForgotPassword from "./components/forgotPassword";
import ResetPassword from "./components/confirmPassword";
import ActiveClient from "./components/admin/activeclients";
import ActiveRoles from "./components/admin/activerole";
import Users from "./components/admin/user";
import Architect from "./components/Architect/architect";
import Project from "./components/Architect/projects";
import NewProject from "./components/Architect/newProject";
import KPI from "./components/Architect/kpi";
import EditProject from "./components/Architect/editProject";
import ColumnToProject from "./components/Architect/columntoproject";
import Threshold from "./components/Architect/threshold";
import Upload from "./components/Architect/fileupload";
import Summary from "./components/Architect/summary";
import History from "./components/Architect/history";
import Marts from "./components/Architect/marts";
import ReportList from "./components/Architect/reports";
import ReportView from "./components/Architect/reportview";
import AgGrid from "./components/Architect/agGrid";
import Sidebar from "./sidebar/sidebar";
import Header from "./reusableField/ReusableHeader";
import NormsSetup from "./components/Architect/norms";
import AllMarts from "./components/Architect/allmarts";
import ViewProjects from "./components/admin/viewProjects"
import { BASE_URL } from "./components/apiConfig";

// Create User Context
const UserContext = createContext();

// Override fetch to handle 401 errors
const originalFetch = window.fetch;
window.fetch = async function (...args) {
  try {
    const response = await originalFetch(...args);
    if (response.status === 401) {
      localStorage.removeItem("accessToken");
      store.dispatch(unSetUserToken());
      window.location.href = "/login-page";
      return Promise.reject(new Error("Unauthorized"));
    }
    return response;
  } catch (error) {
    return Promise.reject(error);
  }
};

const ProtectedLayout = ({
  children,
  isAuthenticated,
  setIsAuthenticated,
  userDetails,
}) => {
  return (
    <Layout style={{ minHeight: "100vh" }}>
      <Sidebar />
      <div style={{ flex: 1 }}>
        <Header
          setIsAuthenticated={setIsAuthenticated}
          userDetails={userDetails}
        />
        {children}
      </div>
    </Layout>
  );
};

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  // Start in loading state so protected routes don't redirect before token hydration completes.
  const [isLoading, setIsLoading] = useState(true);
  const [userType, setUserType] = useState(null);
  const [userDetails, setUserDetails] = useState(null); // Store complete user details
  const { access_token } = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const token = localStorage.getItem("accessToken");
    if (token) {
      setIsLoading(true);
      dispatch(setUserToken({ access_token: token }));
      const fetchUserData = async () => {
        try {
          const response = await fetch(`${BASE_URL}/user/get_user_details`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });
          if (!response.ok) {
            throw new Error("Failed to fetch user data");
          }
          const data = await response.json();
          setUserType(data.user_type);
          setUserDetails(data); // Store complete user details
          setIsAuthenticated(true);
        } catch (error) {
          console.error("Error fetching user data:", error);
          localStorage.removeItem("accessToken");
          dispatch(unSetUserToken());
          setIsAuthenticated(false);
          setUserDetails(null);
          navigate("/login-page");
        } finally {
          setIsLoading(false);
        }
      };
      fetchUserData();
    } else {
      setIsAuthenticated(false);
      setIsLoading(false);
      setUserDetails(null);
      if (
        !location.pathname.includes("login") &&
        !location.pathname.includes("forgot-password") &&
        !location.pathname.includes("resetPassword") &&
        !location.pathname.includes("confirmPassword")
      ) {
        navigate("/login-page", { state: { from: location.pathname } });
      }
    }
  }, [navigate, dispatch]);

  useEffect(() => {
    if (access_token) {
      setIsAuthenticated(true);
    } else {
      setIsAuthenticated(false);
      setUserDetails(null);
    }
  }, [access_token]);

  const getInitialRoute = () => {
    if (!isAuthenticated) return "/login-page";
    return userType === "Project Architect" ? "/architect" : "/dashboard";
  };

  if (isLoading && localStorage.getItem("accessToken")) {
    return (
      <div className="spinner-wrapper">
        <div className="red-spinner" />
      </div>
    );
  }

  return (
    <Provider store={store}>
      <UserContext.Provider value={userDetails}>
        <Routes>
          <Route
            path="/login-page"
            element={
              isAuthenticated ? (
                <Navigate to={getInitialRoute()} replace />
              ) : (
                <LoginPage setIsAuthenticated={setIsAuthenticated} />
              )
            }
          />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/resetPassword" element={<ResetPassword />} />

          <Route
            path="/dashboard"
            element={
              isAuthenticated && userType !== "Project Architect" ? (
                <ProtectedLayout
                  isAuthenticated={isAuthenticated}
                  setIsAuthenticated={setIsAuthenticated}
                  userDetails={userDetails}
                >
                  <ActiveClient />
                </ProtectedLayout>
              ) : (
                <Navigate to={getInitialRoute()} replace />
              )
            }
          />

          <Route
            path="/dashboard/:clientId/roles"
            element={
              isAuthenticated && userType !== "Project Architect" ? (
                <ProtectedLayout
                  isAuthenticated={isAuthenticated}
                  setIsAuthenticated={setIsAuthenticated}
                  userDetails={userDetails}
                >
                  <ActiveRoles />
                </ProtectedLayout>
              ) : (
                <Navigate to={getInitialRoute()} replace />
              )
            }
          />

          <Route
            path="/dashboard/:roleId/viewprojects"
            element={
              isAuthenticated && userType !== "Project Architect" ? (
                <ProtectedLayout
                  isAuthenticated={isAuthenticated}
                  setIsAuthenticated={setIsAuthenticated}
                  userDetails={userDetails}
                >
                  <ViewProjects />
                </ProtectedLayout>
              ) : (
                <Navigate to={getInitialRoute()} replace />
              )
            }
          />

          <Route
            path="/user"
            element={
              isAuthenticated && userType !== "Project Architect" ? (
                <ProtectedLayout
                  isAuthenticated={isAuthenticated}
                  setIsAuthenticated={setIsAuthenticated}
                  userDetails={userDetails}
                >
                  <Users />
                </ProtectedLayout>
              ) : (
                <Navigate to={getInitialRoute()} replace />
              )
            }
          />

          <Route
            path="/architect"
            element={
              isAuthenticated && userType === "Project Architect" ? (
                <ProtectedLayout
                  isAuthenticated={isAuthenticated}
                  setIsAuthenticated={setIsAuthenticated}
                  userDetails={userDetails}
                >
                  <Architect />
                </ProtectedLayout>
              ) : (
                <Navigate to={getInitialRoute()} replace />
              )
            }
          />

          <Route
            path="/architect/:roleId/projects"
            element={
              isAuthenticated && userType === "Project Architect" ? (
                <ProtectedLayout
                  isAuthenticated={isAuthenticated}
                  setIsAuthenticated={setIsAuthenticated}
                  userDetails={userDetails}
                >
                  <Project />
                </ProtectedLayout>
              ) : (
                <Navigate to={getInitialRoute()} replace />
              )
            }
          />

          <Route
            path="/architect/:clientID/:roleId/new-project"
            element={
              isAuthenticated && userType === "Project Architect" ? (
                <ProtectedLayout
                  isAuthenticated={isAuthenticated}
                  setIsAuthenticated={setIsAuthenticated}
                  userDetails={userDetails}
                >
                  <NewProject />
                </ProtectedLayout>
              ) : (
                <Navigate to={getInitialRoute()} replace />
              )
            }
          />

          <Route
            path="/architect/:clientID/:roleId/:projectId/edit-project"
            element={
              isAuthenticated && userType === "Project Architect" ? (
                <ProtectedLayout
                  isAuthenticated={isAuthenticated}
                  setIsAuthenticated={setIsAuthenticated}
                  userDetails={userDetails}
                >
                  <EditProject />
                </ProtectedLayout>
              ) : (
                <Navigate to={getInitialRoute()} replace />
              )
            }
          />

          <Route
            path="/architect/:clientID/:roleId/:projectId/kpi"
            element={
              isAuthenticated && userType === "Project Architect" ? (
                <ProtectedLayout
                  isAuthenticated={isAuthenticated}
                  setIsAuthenticated={setIsAuthenticated}
                  userDetails={userDetails}
                >
                  <KPI />
                </ProtectedLayout>
              ) : (
                <Navigate to={getInitialRoute()} replace />
              )
            }
          />

          <Route
            path="/architect/:clientID/:roleId/:projectId/coloumnto_project"
            element={
              isAuthenticated && userType === "Project Architect" ? (
                <ProtectedLayout
                  isAuthenticated={isAuthenticated}
                  setIsAuthenticated={setIsAuthenticated}
                  userDetails={userDetails}
                >
                  <ColumnToProject />
                </ProtectedLayout>
              ) : (
                <Navigate to={getInitialRoute()} replace />
              )
            }
          />

          <Route
            path="/architect/:projectId/threshold"
            element={
              isAuthenticated && userType === "Project Architect" ? (
                <ProtectedLayout
                  isAuthenticated={isAuthenticated}
                  setIsAuthenticated={setIsAuthenticated}
                  userDetails={userDetails}
                >
                  <Threshold />
                </ProtectedLayout>
              ) : (
                <Navigate to={getInitialRoute()} replace />
              )
            }
          />

          <Route
            path="/architect/:projectId/upload"
            element={
              isAuthenticated && userType === "Project Architect" ? (
                <ProtectedLayout
                  isAuthenticated={isAuthenticated}
                  setIsAuthenticated={setIsAuthenticated}
                  userDetails={userDetails}
                >
                  <Upload />
                </ProtectedLayout>
              ) : (
                <Navigate to={getInitialRoute()} replace />
              )
            }
          />

          <Route
            path="/architect/:inputID/summary"
            element={
              isAuthenticated && userType === "Project Architect" ? (
                <ProtectedLayout
                  isAuthenticated={isAuthenticated}
                  setIsAuthenticated={setIsAuthenticated}
                  userDetails={userDetails}
                >
                  <Summary />
                </ProtectedLayout>
              ) : (
                <Navigate to={getInitialRoute()} replace />
              )
            }
          />

          <Route
            path="/architect/history"
            element={
              isAuthenticated && userType === "Project Architect" ? (
                <ProtectedLayout
                  isAuthenticated={isAuthenticated}
                  setIsAuthenticated={setIsAuthenticated}
                  userDetails={userDetails}
                >
                  <History />
                </ProtectedLayout>
              ) : (
                <Navigate to={getInitialRoute()} replace />
              )
            }
          />

          <Route
            path="/architect/:inputID/marts"
            element={
              isAuthenticated && userType === "Project Architect" ? (
                <ProtectedLayout
                  isAuthenticated={isAuthenticated}
                  setIsAuthenticated={setIsAuthenticated}
                  userDetails={userDetails}
                >
                  <Marts />
                </ProtectedLayout>
              ) : (
                <Navigate to={getInitialRoute()} replace />
              )
            }
          />

          <Route
            path="/architect/:inputID/reports_list"
            element={
              isAuthenticated && userType === "Project Architect" ? (
                <ProtectedLayout
                  isAuthenticated={isAuthenticated}
                  setIsAuthenticated={setIsAuthenticated}
                  userDetails={userDetails}
                >
                  <ReportList />
                </ProtectedLayout>
              ) : (
                <Navigate to={getInitialRoute()} replace />
              )
            }
          />

          <Route
            path="/architect/:inputID/:reportId/:reportCode/reports_view"
            element={
              isAuthenticated && userType === "Project Architect" ? (
                <ProtectedLayout
                  isAuthenticated={isAuthenticated}
                  setIsAuthenticated={setIsAuthenticated}
                  userDetails={userDetails}
                >
                  <ReportView />
                </ProtectedLayout>
              ) : (
                <Navigate to={getInitialRoute()} replace />
              )
            }
          />

          <Route
            path="/agrid"
            element={
              isAuthenticated ? (
                <AgGrid />
              ) : (
                <Navigate to="/login-page" replace />
              )
            }
          />

          <Route
            path="/architect/:inputID/norms"
            element={
              isAuthenticated && userType === "Project Architect" ? (
                <ProtectedLayout
                  isAuthenticated={isAuthenticated}
                  setIsAuthenticated={setIsAuthenticated}
                  userDetails={userDetails}
                >
                  <NormsSetup />
                </ProtectedLayout>
              ) : (
                <Navigate to={getInitialRoute()} replace />
              )
            }
          />
          <Route
            path="/architect/:inputID/allmarts"
            element={
              isAuthenticated && userType === "Project Architect" ? (
                <ProtectedLayout
                  isAuthenticated={isAuthenticated}
                  setIsAuthenticated={setIsAuthenticated}
                  userDetails={userDetails}
                >
                  <AllMarts />
                </ProtectedLayout>
              ) : (
                <Navigate to={getInitialRoute()} replace />
              )
            }
          />

          <Route
            path="/"
            element={<Navigate to={getInitialRoute()} replace />}
          />
        </Routes>
      </UserContext.Provider>
    </Provider>
  );
}

// Export the context for use in other components
export { UserContext };
export default App;
